import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getPaymentsOpsSummary, getPaymentsOpsTrend, getPaymentsOpsDetail,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel,
  fmtInt, fmtAxisDate, fmtTime, safeNum,
} from "./opsShared";

// Ledger TotalAmount: what the card was charged, convenience fee included —
// the same "total = $X" the customer's confirmation quotes.
const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fmtUSD = (v) => USD.format(safeNum(v));

export default function PaymentActivityDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  // Any account row opens its full conversation.
  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary: getPaymentsOpsSummary,
    trend:   getPaymentsOpsTrend,
    detail:  getPaymentsOpsDetail,
  }, params, tzReady);

  const s = data.summary || {};
  const completed    = safeNum(s.paymentsCompleted);
  const scheduled    = safeNum(s.paymentsScheduled);
  const completedAmt = safeNum(s.amountCompleted);
  const scheduledAmt = safeNum(s.amountScheduled);
  // No tile: a counter pinned at 0 is permanent noise on a page people open to
  // read payment volume. Kept as a value because the banner below it — which
  // only appears when the count is non-zero — is the part worth seeing, and
  // Duplicate Watch is where it gets worked.
  const repeats   = safeNum(s.paymentsWithRepeatDelivery);

  const trend = (data.trend || []).map((r) => ({
    date:      fmtAxisDate(r.summary_date, gran, timeZone),
    completed: safeNum(r.paymentsCompleted),
    scheduled: safeNum(r.paymentsScheduled),
  }));

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const columns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "paymentType", label: "Type", width: 100,
      render: (r) => (
        <StatusChip label={r.paymentType} tone={r.paymentType === "Completed" ? "ok" : "default"} />
      ) },
    { key: "customerMessage", label: "Customer's Message", width: 180 },
    { key: "deliveryAttempts", label: "Attempts", width: 90, align: "right",
      render: (r) => {
        const n = safeNum(r.deliveryAttempts);
        return n > 1 ? <StatusChip label={String(n)} tone="bad" /> : n;
      } },
    { key: "receivedUtc",  label: "Received",  width: 160, render: (r) => fmtTime(r.receivedUtc, timeZone) },
    { key: "confirmedUtc", label: "Confirmed", width: 160, render: (r) => fmtTime(r.confirmedUtc, timeZone) },
    { key: "confirmation", label: "Confirmation", width: 360 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Counting payment activity…" />}

      {!loading && (
        <>
          <SectionTitle>Payment Activity</SectionTitle>
          {/* Four columns for two cards on purpose: at half width they stretch
              into banners, and the tile size should match the other Ops pages
              rather than the number of tiles that happen to be left. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Payments Completed" value={fmtInt(completed)} color={GREEN} big
              sub={`${fmtUSD(completedAmt)} charged`} />
            <CounterCard label="Payments Scheduled" value={fmtInt(scheduled)} color={BLUE} big
              sub={`${fmtUSD(scheduledAmt)} future-dated`} />
          </Box>

          {repeats > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              {repeats} payment {repeats === 1 ? "confirmation was" : "confirmations were"}
              {" "}produced on a message that reached Vera more than once. The Duplicate Watch
              tab lists them with the account details needed to check the gateway.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Payments over time")} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  {/* Completed and Scheduled stack because together they are
                      "payment secured" — one arrived, the other is committed. */}
                  <Bar dataKey="completed" name="Completed" stackId="p" fill={GREEN} />
                  <Bar dataKey="scheduled" name="Scheduled" stackId="p" fill={BLUE} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="scheduled" position="top" fontSize={10} fill={BLUE}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Counts and amounts of payment events on the transaction ledger (CF-AI3-PAY
              completed, CF-AI4-PTP scheduled), in any language. Amounts are the total
              charged, including the convenience fee. A row marked "confirmation not sent
              to customer" settled without the customer being told; those are also listed
              on Unconfirmed Payments.
            </Caption>
          </Box>

          <SectionTitle right={`${(data.detail || []).length} shown`}>
            Payment Confirmations
          </SectionTitle>
          <DetailTable
            columns={columns}
            rows={data.detail || []}
            emptyText="No payments in this range."
            maxHeight={480}
          />
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
      />
    </Box>
  );
}